import { useState, useEffect } from 'react';
import { ShoppingCart as CartIcon, Trash2, X, RefreshCw, Plus, Minus, AlertCircle, Lock, ShoppingBag, ArrowRight, Sparkles, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { useCartContext } from '../contexts/CartContext';
import { ProxyImage } from '../lib/imageProxy';

export default function ShoppingCart() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const {
    cartItems,
    loading,
    refreshCart,
    removeFromCartLocal,
    updateQuantityLocal
  } = useCartContext();
  const [userPriceType, setUserPriceType] = useState<'contado' | 'credito' | 'mayorista' | 'publico' | null>(null);
  const [user, setUser] = useState<any>(null);

  const fetchUserPriceType = async (userId?: string) => {
    try {
      const uid = userId || (await supabase.auth.getUser()).data.user?.id;
      if (!uid) {
        setUserPriceType(null);
        return;
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('price_type')
        .eq('id', uid)
        .single();

      if (error) throw error;
      setUserPriceType(profile?.price_type || null);
    } catch (err) {
      console.error('Error fetching user price type:', err);
    }
  };

  useEffect(() => {
    // Obtener usuario inicial
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
      if (user) {
        fetchUserPriceType(user.id);
      }
    });

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserPriceType(session.user.id);
      } else {
        setUserPriceType(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (isOpen) {
      refreshCart();
    }
  }, [isOpen, refreshCart]);

  const removeFromCart = async (itemId: string) => {
    try {
      setUpdatingItems(prev => new Set(prev).add(itemId));

      if (!user) {
        const item = cartItems.find(item => item.id === itemId);
        if (item) {
          removeFromCartLocal(item.product_id);
        }
        return;
      }

      const { data: cart } = await supabase
        .from('cart')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!cart) return;

      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', itemId)
        .eq('cart_id', cart.id);

      if (error) throw error;
      await refreshCart();
    } catch (error) {
      console.error('Error removing item:', error);
    } finally {
      setUpdatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const getPriceInfo = (item: typeof cartItems[0]) => {
    const type = user ? (userPriceType || 'publico') : 'publico';

    if (!item.product.prices || item.product.prices.length === 0) {
      return { current: item.product.base_price, original: null } as { current: number; original: number | null };
    }

    const applicable = item.product.prices.filter(p => p.price_type === type);
    if (applicable.length === 0) {
      return { current: item.product.base_price, original: null };
    }

    const valid = applicable.filter(p => item.quantity >= p.min_quantity);
    // Seleccionar el rango con min_quantity más alto que aplique (ordenar descendente)
    const selected = valid.length > 0
      ? [...valid].sort((a, b) => b.min_quantity - a.min_quantity)[0]
      : [...applicable].sort((a, b) => a.min_quantity - b.min_quantity)[0];

    if (!selected) {
      return { current: item.product.base_price, original: null };
    }

    if (selected.offer_price != null) {
      return { current: selected.offer_price, original: selected.price };
    }
    return { current: selected.price, original: null };
  };

  const getProductPrice = (item: typeof cartItems[0]) => {
    return getPriceInfo(item).current;
  };

  const handleCheckout = async () => {
    try {
      setIsSubmitting(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setIsOpen(false);
        navigate('/registro-cliente');
        return;
      }

      setIsOpen(false);
      navigate('/order-summary');
    } catch (err) {
      console.error('Error al procesar el checkout:', err);
      if (err instanceof Error) {
        alert(`Error al procesar el checkout: ${err.message}`);
      } else {
        alert('Error al procesar el checkout. Por favor, intenta de nuevo.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCartTotal = () => {
    let total = 0;
    for (const item of cartItems) {
      const pricePerUnit = getProductPrice(item);
      const itemTotal = pricePerUnit * item.quantity;
      total += itemTotal;
    }
    return total;
  };

  const getTotalItems = () => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  const forceUpdatePrices = async () => {
    setIsRefreshing(true);
    try {
      await refreshCart();
    } catch (error) {
      console.error('Error refreshing cart:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getMinQuantity = (item: typeof cartItems[0]) => {
    const effectiveType = user ? (userPriceType || 'publico') : 'publico';

    if (effectiveType && item.product.prices && Array.isArray(item.product.prices)) {
      const userPrices = item.product.prices.filter(p => p.price_type === effectiveType);
      if (userPrices.length > 0) {
        const sortedUserPrices = [...userPrices].sort((a, b) => a.min_quantity - b.min_quantity);
        const minQuantityPrice = sortedUserPrices[0];
        if (minQuantityPrice && minQuantityPrice.min_quantity) {
          return minQuantityPrice.min_quantity;
        }
      }
    }

    return 1;
  };

  const updateItemQuantity = async (itemId: string, newQuantity: number, minQuantity: number) => {
    if (newQuantity < minQuantity) return;

    try {
      setUpdatingItems(prev => new Set(prev).add(itemId));

      if (!user) {
        const item = cartItems.find(item => item.id === itemId);
        if (item) {
          updateQuantityLocal(item.product_id, newQuantity);
        }
        return;
      }

      const { error } = await supabase
        .from('cart_items')
        .update({
          quantity: newQuantity,
          updated_at: new Date().toISOString()
        })
        .eq('id', itemId);

      if (error) throw error;
      await refreshCart();
    } catch (error) {
      console.error('Error updating item quantity:', error);
    } finally {
      setUpdatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const effectivePriceType = user ? (userPriceType || 'publico') : 'publico';

  return (
    <div>
      {/* Cart Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className="relative p-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
        aria-label="Abrir carrito de compras"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <CartIcon className="h-6 w-6" />
        <AnimatePresence>
          {cartItems.length > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold"
            >
              {cartItems.length > 9 ? '9+' : cartItems.length}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Cart Drawer */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />

            {/* Drawer */}
            <motion.div
              className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Mi Carrito</h2>
                    <p className="text-sm text-gray-500">
                      {getTotalItems()} {getTotalItems() === 1 ? 'producto' : 'productos'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={forceUpdatePrices}
                    disabled={isRefreshing}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                    title="Actualizar"
                  >
                    <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="Cerrar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Price Type Indicator */}
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Lista de precios:</span>
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                    effectivePriceType === 'mayorista' ? 'bg-purple-100 text-purple-700' :
                    effectivePriceType === 'contado' ? 'bg-green-100 text-green-700' :
                    effectivePriceType === 'credito' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {effectivePriceType === 'mayorista' ? 'Mayorista' :
                     effectivePriceType === 'contado' ? 'Contado' :
                     effectivePriceType === 'credito' ? 'Crédito' : 'Público'}
                  </span>
                </div>
              </div>

              {/* Content */}
              {loading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-gray-500">Cargando...</p>
                  </div>
                </div>
              ) : cartItems.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <ShoppingBag className="w-10 h-10 text-gray-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Tu carrito está vacío</h3>
                  <p className="text-sm text-gray-500 mb-6">Agrega productos para comenzar tu compra</p>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/productos');
                    }}
                    className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Ver productos
                  </button>
                </div>
              ) : (
                <>
                  {/* Auth Alert */}
                  {!user && (
                    <div className="px-6 py-3 bg-amber-50 border-b border-amber-100">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                        <p className="text-sm text-amber-700">
                          <span className="font-medium">Regístrate</span> para completar tu compra
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Items List */}
                  <div className="flex-1 overflow-y-auto">
                    <div className="divide-y divide-gray-100">
                      <AnimatePresence initial={false}>
                        {cartItems.map((item) => {
                          if (!item || !item.product) return null;

                          const priceInfo = getPriceInfo(item);
                          const itemTotal = priceInfo.current * item.quantity;
                          const minQuantity = getMinQuantity(item);
                          const isUpdating = updatingItems.has(item.id);
                          const hasOffer = priceInfo.original !== null;
                          const discount = hasOffer ? Math.round((1 - priceInfo.current / priceInfo.original!) * 100) : 0;

                          return (
                            <motion.div
                              key={item.id}
                              layout
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, x: -100 }}
                              className={`relative p-4 ${isUpdating ? 'opacity-50' : ''}`}
                            >
                              <div className="flex gap-4">
                                {/* Image */}
                                <div className="relative w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                  <ProxyImage
                                    src={item.product.main_image}
                                    alt={item.product.name}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=Sin+imagen';
                                    }}
                                  />
                                  {hasOffer && (
                                    <div className="absolute top-1 left-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                                      -{discount}%
                                    </div>
                                  )}
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <h4 className="text-sm font-medium text-gray-900 line-clamp-2 leading-tight">
                                      {item.product.name}
                                    </h4>
                                    <button
                                      onClick={() => removeFromCart(item.id)}
                                      disabled={isUpdating}
                                      className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                      aria-label="Eliminar"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>

                                  {/* Price */}
                                  <div className="mt-1 flex items-center gap-2">
                                    {hasOffer ? (
                                      <>
                                        <span className="text-sm font-semibold text-red-600">
                                          Q{priceInfo.current.toFixed(2)}
                                        </span>
                                        <span className="text-xs text-gray-400 line-through">
                                          Q{priceInfo.original!.toFixed(2)}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="text-sm font-semibold text-gray-900">
                                        Q{priceInfo.current.toFixed(2)}
                                      </span>
                                    )}
                                    <span className="text-xs text-gray-400">c/u</span>
                                  </div>

                                  {/* Quantity & Total */}
                                  <div className="mt-3 flex items-center justify-between">
                                    <div className="flex items-center border border-gray-200 rounded-lg">
                                      <button
                                        onClick={() => updateItemQuantity(item.id, item.quantity - 1, minQuantity)}
                                        disabled={item.quantity <= minQuantity || isUpdating}
                                        className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-l-lg"
                                      >
                                        <Minus className="w-3.5 h-3.5" />
                                      </button>
                                      <span className="w-10 text-center text-sm font-medium text-gray-900">
                                        {item.quantity}
                                      </span>
                                      <button
                                        onClick={() => updateItemQuantity(item.id, item.quantity + 1, minQuantity)}
                                        disabled={isUpdating}
                                        className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-30 transition-colors rounded-r-lg"
                                      >
                                        <Plus className="w-3.5 h-3.5" />
                                      </button>
                                    </div>

                                    <div className="text-right">
                                      <span className={`text-sm font-bold ${hasOffer ? 'text-red-600' : 'text-gray-900'}`}>
                                        Q{itemTotal.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Min quantity hint */}
                                  {minQuantity > 1 && (
                                    <p className="mt-1.5 text-[11px] text-gray-400">
                                      Mín: {minQuantity} unidades
                                    </p>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="border-t border-gray-100 bg-white">
                    {/* Shipping info */}
                    <div className="px-6 py-3 bg-gray-50">
                      <div className="flex items-center gap-2 text-sm">
                        <Truck className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-600">El envío se cobra al momento de la entrega</span>
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="px-6 py-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="text-lg font-bold text-gray-900">
                          Q{getCartTotal().toFixed(2)}
                        </span>
                      </div>

                      {/* Checkout Button */}
                      <button
                        onClick={handleCheckout}
                        disabled={isSubmitting}
                        className="w-full py-3.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Procesando...
                          </>
                        ) : !user ? (
                          <>
                            <Lock className="w-5 h-5" />
                            Iniciar sesión
                          </>
                        ) : (
                          <>
                            Continuar compra
                            <ArrowRight className="w-5 h-5" />
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => setIsOpen(false)}
                        className="w-full py-2.5 text-gray-600 text-sm font-medium hover:text-gray-900 transition-colors"
                      >
                        Seguir comprando
                      </button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
